/**
 * 整段离线录音：MediaRecorder + Web Audio 音量阈值 VAD。
 * 录完再交给 /api/asr，不做边说边出字。
 */


export type VoiceRecorderOptions = {
  /** RMS 低于此值视为静音（0~1） */
  silenceRms?: number;
  /** 连续静音多久自动停（ms） */
  silenceMs?: number;
  /** 最短有效说话时长，避免一开口就因短暂静音停（ms） */
  minSpeechMs?: number;
  /** 最长录音（ms） */
  maxMs?: number;
  /** 电平回调 0~1，供波形 UI */
  onLevel?: (level: number) => void;
  /**
   * 关掉浏览器的降噪/回声消除/自动增益。
   *
   * 语音输入要开（识别更准），**音色克隆必须关**：这三样都会改变音色本身，
   * 自动增益还会把气声和尾音抬起来，克隆出来的声音会跟本人不像。
   * 代价是对录音环境的要求更高，所以录制界面要给环境指引。
   */
  rawAudio?: boolean;
};

const DEFAULTS = {
  silenceRms: 0.018,
  silenceMs: 1600,
  minSpeechMs: 500,
  maxMs: 60_000,
  rawAudio: false
};

export class VoiceRecorder {
  private stream: MediaStream | null = null;
  private recorder: MediaRecorder | null = null;
  private chunks: Blob[] = [];
  private audioCtx: AudioContext | null = null;
  private analyser: AnalyserNode | null = null;
  private raf = 0;
  private startedAt = 0;
  private lastVoiceAt = 0;
  private heardVoice = false;
  private maxTimer: ReturnType<typeof setTimeout> | null = null;
  private options: Required<Omit<VoiceRecorderOptions, "onLevel">> & Pick<VoiceRecorderOptions, "onLevel">;
  private resolveStop: ((blob: Blob | null) => void) | null = null;
  /** VAD/超时自动 stop 后，供稍后 stop() 取回 */
  private lastBlob: Blob | null = null;
  private stopPromise: Promise<Blob | null> | null = null;

  constructor(options: VoiceRecorderOptions = {}) {
    this.options = { ...DEFAULTS, ...options };
  }

  get active() {
    return this.recorder?.state === "recording";
  }

  async start(): Promise<void> {
    if (this.active) return;

    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
      throw new Error("unsupported");
    }

    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: this.options.rawAudio
          ? { echoCancellation: false, noiseSuppression: false, autoGainControl: false }
          : { echoCancellation: true, noiseSuppression: true, autoGainControl: true }
      });
    } catch (e) {
      const name = e instanceof DOMException ? e.name : "";
      if (name === "NotAllowedError" || name === "PermissionDeniedError") {
        throw new Error("permission_denied");
      }
      if (name === "NotFoundError" || name === "DevicesNotFoundError") {
        throw new Error("no_mic");
      }
      throw e;
    }

    this.stream = stream;
    this.chunks = [];
    this.lastBlob = null;
    this.stopPromise = null;
    this.heardVoice = false;
    this.startedAt = Date.now();
    this.lastVoiceAt = this.startedAt;

    const preferred = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg"];
    const mimeType = preferred.find((t) => MediaRecorder.isTypeSupported?.(t));
    const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
    this.recorder = recorder;

    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) this.chunks.push(e.data);
    };

    recorder.onstop = () => {
      this.teardownGraph();
      const raw =
        this.chunks.length > 0
          ? new Blob(this.chunks, { type: recorder.mimeType || "audio/webm" })
          : null;
      this.lastBlob = raw;
      const resolve = this.resolveStop;
      this.resolveStop = null;
      this.stopPromise = null;
      resolve?.(raw);
    };

    this.setupVad(stream);
    recorder.start(200);

    if (this.maxTimer) clearTimeout(this.maxTimer);
    this.maxTimer = setTimeout(() => {
      void this.stop();
    }, this.options.maxMs);
  }

  /** 停止录音，返回原始 blob（可能很小）；调用方再转 WAV / 识别 */
  stop(): Promise<Blob | null> {
    // 已停过（VAD/超时）：直接返回缓存 blob
    if (!this.recorder || this.recorder.state === "inactive") {
      this.teardownGraph();
      return this.stopPromise ?? Promise.resolve(this.lastBlob);
    }
    if (this.maxTimer) {
      clearTimeout(this.maxTimer);
      this.maxTimer = null;
    }
    if (this.stopPromise) return this.stopPromise;
    this.stopPromise = new Promise((resolve) => {
      this.resolveStop = resolve;
      try {
        this.recorder?.stop();
      } catch {
        this.teardownGraph();
        this.stopPromise = null;
        resolve(this.lastBlob);
      }
    });
    return this.stopPromise;
  }

  /** 丢弃并释放，不关心 blob */
  async cancel() {
    await this.stop();
  }

  private setupVad(stream: MediaStream) {
    const AudioCtx: typeof AudioContext =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    const ctx = new AudioCtx();
    this.audioCtx = ctx;
    const source = ctx.createMediaStreamSource(stream);
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 2048;
    analyser.smoothingTimeConstant = 0.8;
    source.connect(analyser);
    this.analyser = analyser;

    const data = new Float32Array(analyser.fftSize);
    const tick = () => {
      if (!this.analyser || this.recorder?.state !== "recording") return;
      this.analyser.getFloatTimeDomainData(data);
      let sum = 0;
      for (let i = 0; i < data.length; i++) sum += data[i] * data[i];
      const rms = Math.sqrt(sum / data.length);
      // 映射到 0~1 给 UI（稍微放大一点更好看）
      const level = Math.min(1, rms * 8);
      this.options.onLevel?.(level);

      const now = Date.now();
      if (rms >= this.options.silenceRms) {
        this.heardVoice = true;
        this.lastVoiceAt = now;
      } else if (
        this.heardVoice &&
        now - this.startedAt >= this.options.minSpeechMs &&
        now - this.lastVoiceAt >= this.options.silenceMs
      ) {
        void this.stop();
        return;
      }
      this.raf = requestAnimationFrame(tick);
    };
    this.raf = requestAnimationFrame(tick);
  }

  private teardownGraph() {
    if (this.raf) {
      cancelAnimationFrame(this.raf);
      this.raf = 0;
    }
    if (this.maxTimer) {
      clearTimeout(this.maxTimer);
      this.maxTimer = null;
    }
    if (this.audioCtx) {
      void this.audioCtx.close().catch(() => undefined);
      this.audioCtx = null;
    }
    this.analyser = null;
    if (this.stream) {
      this.stream.getTracks().forEach((t) => t.stop());
      this.stream = null;
    }
    this.recorder = null;
  }
}

/** 原始录音 → base64，供 POST /api/asr。
 *
 *  不再转 WAV：服务端已加 ffmpeg 兜底解码，直接吃 MediaRecorder 的
 *  opus/aac。实测同一段语音 WAV 291KB vs opus 14KB（21 倍），识别结果一致，
 *  而 fire→ECS 隧道只有 2~9 KB/s，上传体积直接等于等待时间。 */
export async function blobToAsrBase64(raw: Blob): Promise<string> {
  const buf = await raw.arrayBuffer();
  let binary = "";
  const bytes = new Uint8Array(buf);
  const CHUNK = 8192;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode.apply(null, Array.from(bytes.subarray(i, i + CHUNK)));
  }
  return btoa(binary);
}
